/**
 * Frido Combo Tiers runtime — renders quantity-bundle tier cards from the
 * BASE Spectrum contract only: spectrum.quantity_bundles_live slot payload →
 * default entry → content.reward.tiers. Tier title/minQuantity/discount/
 * badges/timer come from the bundle data. The coupon chip is a coupon-shaped
 * label in a tier's badges[] OR a payable-price threshold rule given via the
 * `coupon_rules` prop (no hardcoded default). presentation knobs (show_timer,
 * label_prefix, show_save, coupon_rules) are props.
 *
 * Option-axis priced rows and buying-options groups are fcbfrd02 —
 * this component renders bundle DATA only, by contract.
 */
;(() => {
  if (window.__sai_fcbfrd01_initialized__) return
  window.__sai_fcbfrd01_initialized__ = true

  const SNIPPET_ID = 'fcbfrd01'
  const TAG = 'sai-fcbfrd01'
  const FEATURE_SLUG = 'quantity_bundles'

  function esc(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
  function readPool(node) {
    for (let i = 0; i < node.children.length; i++) {
      const c = node.children[i]
      if (c.tagName === 'SCRIPT' && c.hasAttribute('data-spectrum-snippet-pool')) {
        try { return JSON.parse(c.textContent) } catch (e) { return {} }
      }
    }
    return {}
  }
  function fmtMoney(major, cur) {
    if (major == null || Number.isNaN(Number(major))) return ''
    return (cur || '₹') + Math.round(Number(major)).toLocaleString('en-IN')
  }
  function timerState(key, minutes) {
    const total = minutes * 60 * 1000
    let start = 0
    try { start = Number(localStorage.getItem(key)) || 0 } catch (e) {}
    const now = Date.now()
    if (!start || now - start >= total) {
      start = now
      try { localStorage.setItem(key, String(start)) } catch (e) {}
    }
    return { remaining: total - (now - start), total }
  }
  function fmtClock(ms) {
    const s = Math.max(0, Math.floor(ms / 1000))
    const h = String(Math.floor(s / 3600)).padStart(2, '0')
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
    const sec = String(s % 60).padStart(2, '0')
    return `${h}<small>h</small>&nbsp; ${m}<small>m</small> ${sec}<small>s</small>`
  }
  function helpSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.34c-.7.27-1.4.83-1.4 1.66v.5"/><circle cx="11.9" cy="16.6" r=".5" fill="currentColor"/></svg>'
  }
  function arrowSvg() {
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="13 6 19 12 13 18"/></svg>'
  }

  const COUPON_RE = /^(free\s*)?coupon/i

  // tier.badges[] → { badge: black chip text, coupon: chip text } split
  function splitBadges(badges) {
    const out = { badge: '', coupon: '' }
    for (const b of badges || []) {
      const label = (b && b.label) || ''
      if (!label) continue
      if (COUPON_RE.test(label.replace(/^free\s*/i, '')) || /^free/i.test(label)) {
        out.coupon = label
      } else if (!out.badge) {
        out.badge = label
      }
    }
    return out
  }

  // Normalize the coupon_rules prop: JSON-string or array in, validated
  // [{min, label}] out, sorted min-desc so find() returns the highest match.
  // The rule is authored PER PLACEMENT via the `coupon_rules` prop — there is
  // no hardcoded default (empty/unset = no computed chips).
  function couponRulesFrom(props) {
    let rules = props && props.coupon_rules
    if (typeof rules === 'string') {
      try { rules = JSON.parse(rules) } catch (e) { rules = null }
    }
    if (!Array.isArray(rules)) return []
    return rules
      .filter((r) => r && Number(r.min) > 0 && r.label)
      .sort((a, b) => Number(b.min) - Number(a.min))
  }

  function tierPrice(pool, tier) {
    const qty = Number(tier.minQuantity) || 1
    const unitMinor = Number(pool.product.price) || 0
    const capMinor = Number(pool.product.compare_at) || 0
    const gross = (unitMinor * qty) / 100
    let price = gross
    if (tier.discountValueType === 'FIXED' && tier.discountFixedAmount) {
      price = Math.max(0, gross - Number(tier.discountFixedAmount))
    } else if (tier.discountPercentage) {
      price = gross * (1 - Number(tier.discountPercentage) / 100)
    }
    const mrp = capMinor > unitMinor ? (capMinor * qty) / 100 : (price < gross ? gross : null)
    return { qty, price, mrp, save: mrp && mrp > price ? mrp - price : null }
  }

  function buildRows(pool) {
    // ── quantity tiers (base bundle contract)
    const bundle = pool.bundle
    // A bundle the merchant deactivated must render nothing. The slot keeps
    // serving its content after deactivation, so honour the bundle's own
    // status — the Liquid guards the server render, this guards re-renders.
    if (bundle && bundle.status && bundle.status !== 'active') return []
    const rawTiers = bundle && bundle.reward && Array.isArray(bundle.reward.tiers) ? bundle.reward.tiers : []
    // A quantity combo shows exactly one card per quantity. Source feeds
    // sometimes carry duplicate entries for the same minQuantity (a second copy
    // with a different badge / zero discount); collapse to the first, richest
    // occurrence so two "2 Pairs" cards can never render.
    const seenQty = new Set()
    const tiers = rawTiers.filter((t) => {
      const q = Number(t.minQuantity) || 1
      if (seenQty.has(q)) return false
      seenQty.add(q)
      return true
    })
    // The slot flatten drops zero-discount tiers, but the shopper always sees
    // the base "buy one" row (live parity). Synthesize it from the next tier's
    // title (strip the count, singularize) when qty-1 is absent; placements
    // can override the label via props.base_tier_title.
    if (tiers.length && !seenQty.has(1)) {
      const nextTitle = String(tiers[0].title || '')
      // Synthesize the "buy one" title from the next tier: swap the FIRST number
      // (anywhere in the string, so "Buy 2 Pairs" → "Buy 1 Pairs") for 1, then
      // singularize a trailing plural unit when the count is now 1 → "Buy 1 Pair".
      let baseTitle = (pool.props && pool.props.base_tier_title) || nextTitle.replace(/\d+/, '1')
      // singularize the unit word right after the "1" (handles "1 Pairs (Left + Right)"
      // where the plural is mid-string, not just a trailing "s")
      baseTitle = baseTitle.replace(/(^|\s)(1\s+[A-Za-z]+?)s\b/, '$1$2')
      tiers.unshift({ triggerType: 'QUANTITY', minQuantity: 1, discountValueType: 'FIXED', discountFixedAmount: '0', title: baseTitle, badges: [] })
    }
    // Coupon chip: a coupon-shaped label authored in the tier's badges[] takes
    // precedence; otherwise the placement's `coupon_rules` prop (payable-price
    // thresholds [{min,label}], highest match wins) applies — but ONLY when the
    // product is coupon-enabled. Without that gate the thresholds would paint a
    // coupon on every product whose tier prices cross them, including the ones the
    // storefront shows none on.
    const couponRules = couponRulesFrom(pool.props)
    // Per-product coupon switch (mirrors Tectonic's free-coupon-nudge entity),
    // seeded on a tier's publicMetadata.couponEnabled. Product-level: true if any
    // tier carries it. Absent/false ⇒ no computed coupon chips anywhere.
    // publicMetadata values arrive as STRINGS (tierMetadataValueSchema = z.string(),
    // and the bulk-CSV ingest stores cells verbatim), so accept the string "true"
    // as well as a boolean true from a direct write.
    const couponEnabled = tiers.some((t) => {
      const v = (t.publicMetadata || {}).couponEnabled
      return v === true || v === 'true'
    })
    return tiers.map((tier, i) => {
      const { qty, price, mrp, save } = tierPrice(pool, tier)
      let { badge, coupon } = splitBadges(tier.badges)
      // Dedicated per-tier metadata fields (durable, D1-sourced via publicMetadata)
      // are the authoritative source: `coupon` label + `timer` minutes are authored
      // as their own key-value pairs, independent of the badge chip. They take
      // precedence over a coupon-shaped badge label and the coupon_rules prop.
      const pm = tier.publicMetadata || {}
      if (pm.coupon) coupon = String(pm.coupon)
      if (!coupon && couponEnabled && couponRules.length) {
        const rule = couponRules.find((r) => price >= Number(r.min))
        if (rule) coupon = String(rule.label)
      }
      // Append the tier's total discount to the badge ("Best Seller 79 % OFF"),
      // matching the storefront. Gated by show_badge_discount (default on) and only
      // when a badge and a real discount both exist; the base tier (zero discount)
      // and unbadged tiers stay bare.
      if (badge && (pool.props ? pool.props.show_badge_discount !== false : true) && save > 0 && mrp > 0) {
        const pct = Math.round((save / mrp) * 100)
        if (pct > 0) badge = `${badge} ${pct} % OFF`
      }
      return {
        idx: i, kind: 'qty', qty,
        title: tier.title || `${qty} Item${qty > 1 ? 's' : ''}`,
        price, mrp, save, badge, coupon,
        // Countdown minutes are per-tier authored data in publicMetadata.timer
        // (durable, D1-sourced). Falls back to a top-level tier.timer for legacy
        // payloads. A tier with neither shows no countdown.
        timer: Number(pm.timer) || Number(tier.timer) || 0,
        isDefaultTier: bundle.defaultTierMinQuantity != null && qty === Number(bundle.defaultTierMinQuantity),
      }
    })
  }



  const El = class extends HTMLElement {
    connectedCallback() {
      if (this._init) return
      this._init = true
      this._track = () => {}
      this._timers = []
      // Tier prices key off the CURRENT variant's unit price — re-derive on
      // in-place variant switches.
      this._onVariantChange = (e) => {
        const vid = e && e.detail && e.detail.variantId
        if (!vid || !this._pool || !this._pool.product) return
        const v = (this._pool.product.variants || []).find((x) => String(x.id) === String(vid))
        if (!v) return
        this._pool.product.price = v.price
        this._pool.product.compare_at = v.compare_at
        this._rows = buildRows(this._pool)
        if (this._rows.length) this._render()
      }
      window.addEventListener('spectrum:variant-change', this._onVariantChange)
    }
    disconnectedCallback() {
      this._clearTimers()
      window.removeEventListener('spectrum:variant-change', this._onVariantChange)
    }
    setAnalytics(t) { this._track = typeof t === 'function' ? t : () => {} }
    _clearTimers() { for (const t of this._timers) clearInterval(t); this._timers = [] }

    applyVariant(_content, pool) {
      this._pool = pool && typeof pool === 'object' ? pool : {}
      this._rows = buildRows(this._pool)
      if (!this._rows.length) return
      if (this._selected == null) {
        const byDef = this._rows.findIndex((r) => r.isDefaultTier)
        this._selected = byDef >= 0 ? byDef : 0
        this._syncQuantity(this._rows[this._selected])
      }
      this._render()
      this._bind()
    }

    _props() { return (this._pool && this._pool.props) || {} }

    _label() {
      const p = this._props()
      if (p.label_prefix) return p.label_prefix
      if (this._pool.bundle && this._pool.bundle.heading) return this._pool.bundle.heading + ' :'
      return 'COMBO :'
    }

    _render() {
      const props = this._props()
      const cur = this._pool.product.currency
      const selRow = this._rows[this._selected]
      const label = `<div class="sai-fcbfrd01__label"><span class="sai-fcbfrd01__label-key">${esc(this._label())}</span><span class="sai-fcbfrd01__label-val" data-sel-label>${esc(selRow ? selRow.title : '')}</span></div>`

      let rows = '<div class="sai-fcbfrd01__rows">'
      for (let i = 0; i < this._rows.length; i++) {
        const r = this._rows[i]
        const selected = i === this._selected
        const badge = r.badge
          ? `<div class="sai-fcbfrd01__badge">${esc(r.badge).replace(/(\d+\s*% OFF)/i, '<b>$1</b>')}</div>` : ''
        // SAVE never shows on the base (lowest-quantity, index 0) tier — the
        // storefront + Figma only show it on the higher volume tiers.
        const showSave = props.show_save !== false && i > 0
        const save = r.save && showSave ? `<span class="sai-fcbfrd01__save">${props.save_label || 'SAVE'} ${fmtMoney(r.save, cur)}</span>` : ''
        const mrp = r.mrp && r.mrp > r.price ? `<span class="sai-fcbfrd01__mrp">${props.mrp_label || 'MRP'} <s>${fmtMoney(r.mrp, cur)}</s></span>` : ''
        let coupon = ''
        if (r.coupon) {
          const main = r.coupon.replace(/^free\s*/i, '')
          const m = main.match(/^(.*?)(₹?[\d,]+)?\s*$/)
          coupon = `<span class="sai-fcbfrd01__coupon"><span class="sai-fcbfrd01__coupon-free">${esc(props.coupon_free_label || 'FREE')}</span><span class="sai-fcbfrd01__coupon-main"><span class="lbl">${esc((m && m[1] || main).trim())}</span>${m && m[2] ? `<span class="amt">${esc(m[2])}</span>` : ''}</span></span>`
        }
        // countdown renders on whichever tier carries a `timer` (authored on
        // the default/best tier); a placement-wide `show_timer:false` prop can
        // still suppress it.
        const showTimer = r.timer > 0 && props.show_timer !== false
        const timer = showTimer
          ? `<div class="sai-fcbfrd01__timer" data-timer-key="sai-frido-timer:${esc(this._pool.product.handle)}:combo:${i}" data-timer-min="${r.timer}"><span class="sai-fcbfrd01__timer-label">${esc(props.timer_label || 'Limited Time Offer')}</span><span class="sai-fcbfrd01__timer-clock" data-clock></span></div>` : ''

        let rowInner
        {
          const thumb = r.image ? `<img class="sai-fcbfrd01__row-thumb" src="${esc(r.image)}" alt="" loading="lazy" decoding="async">` : ''
          // "+" joins the tier title to its free-coupon bonus ("Buy 1 Pillow +
          // FREE COUPON ₹500"), matching the storefront. Only shown when the
          // tier carries a coupon — a plain tier has no separator.
          const plus = coupon ? '<span class="sai-fcbfrd01__plus" aria-hidden="true">+</span>' : ''
          rowInner = `<span class="sai-fcbfrd01__row-left">${thumb}<span class="sai-fcbfrd01__title${r.image ? ' sai-fcbfrd01__title--sm' : ''}">${esc(r.title)}</span>${plus}${coupon}</span>
            <span class="sai-fcbfrd01__row-right">${save}<span class="sai-fcbfrd01__prices">${mrp}<span class="sai-fcbfrd01__price">${fmtMoney(r.price, cur)}</span></span></span>`
        }

        rows += `<div class="sai-fcbfrd01__unit">${badge}
          <button type="button" class="sai-fcbfrd01__row${selected ? ' is-sel' : ''}" data-action="pick" data-idx="${i}">${rowInner}</button>${timer}</div>`
      }
      rows += '</div>'

      let help = ''
      if (props.show_need_help === true) {
        help = `<div class="sai-fcbfrd01__help" data-action="help" role="button" tabindex="0">
          <div class="sai-fcbfrd01__help-left">${helpSvg()}<div><div class="sai-fcbfrd01__help-title">${esc(props.need_help_title || 'Need help choosing a variant')}</div><div class="sai-fcbfrd01__help-sub">${esc(props.need_help_subtitle || 'Compare and discover')}</div></div></div>
          <span style="color:var(--f-blue);display:inline-flex">${arrowSvg()}</span>
        </div>`
      }

      this.innerHTML = `<div class="sai-fcbfrd01__inner">${label}${rows}${help}</div>`
      this._startTimers()
    }

    _startTimers() {
      this._clearTimers()
      for (const el of this.querySelectorAll('[data-timer-key]')) {
        const key = el.dataset.timerKey
        const min = Number(el.dataset.timerMin) || 0
        if (!min) continue
        const clock = el.querySelector('[data-clock]')
        const tick = () => { if (clock) clock.innerHTML = fmtClock(timerState(key, min).remaining) }
        tick()
        this._timers.push(setInterval(tick, 1000))
      }
    }


    _syncQuantity(row) {
      if (!row || row.kind !== 'qty' || this._props().apply_quantity_to_form === false) return
      const qtyInput = document.querySelector('form[action*="/cart/add"] input[name="quantity"], form[action*="/cart/add"] quantity-input input')
      if (qtyInput) {
        qtyInput.value = String(row.qty)
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    _bind() {
      if (this._clickBound) return
      this._clickBound = true
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-action]')
        if (!t) return
        if (t.dataset.action === 'pick') return this._pick(Number(t.dataset.idx))
        if (t.dataset.action === 'help') {
          window.dispatchEvent(new CustomEvent('spectrum:compare-open', {}))
          this._track(`${FEATURE_SLUG}:need_help_elem_clicked`, {})
        }
      })
    }

    _pick(i) {
      const row = this._rows[i]
      if (!row || i === this._selected) return
      this._selected = i
      this._render()

      this._syncQuantity(row)
      window.dispatchEvent(new CustomEvent('spectrum:combo-change', { detail: { index: i, quantity: row.qty, title: row.title } }))
      this._track(`${FEATURE_SLUG}:select_combo`, { index: i, kind: row.kind, title: row.title })
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, El)

  function bindAll() {
    const api = window.__spectrumAi && window.__spectrumAi.snippet
    for (const node of document.querySelectorAll(`[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`)) {
      const el = node.querySelector(TAG)
      if (!el) continue
      // First paint straight from the server-rendered pool — the pool's props
      // are the instance's default config, so rendering never waits on the
      // SDK (which may load after DOMContentLoaded or resolve asynchronously).
      if (!el._painted) {
        const initPool = readPool(node)
        if (initPool && (initPool.product || initPool.bundle || initPool.axis || initPool.buying_options)) { el._painted = true; el.applyVariant({}, initPool) }
      }
      if (api && typeof api.bind === 'function' && !el._wrapBound) {
        el._wrapBound = true
        const handles = api.bind(node, (ctx) => {
          const e = node.querySelector(TAG)
          if (e) e.applyVariant(ctx.variant ? ctx.variant.content : {}, readPool(node))
        })
        if (handles) el.setAnalytics(handles.track)
      }
    }
    for (const el of document.querySelectorAll(TAG)) {
      if (el._applied || el._wrapBound || el.closest('[data-spectrum-instance-id]')) continue
      const parent = el.closest('[data-spectrum-lq-snippet]') || el.parentElement
      if (!parent) continue
      const pool = readPool(parent)
      if (pool && pool.bundle) { el._applied = true; el.applyVariant({}, pool) }
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFcbfrd01 = { buildRows, splitBadges, tierPrice, timerState, fmtClock, fmtMoney }
  }

  function schedule() {
    bindAll()
    // the SDK can load after us — retry until it binds (or give up quietly)
    let tries = 0
    const t = setInterval(() => {
      bindAll()
      const api = window.__spectrumAi && window.__spectrumAi.snippet
      if ((api && typeof api.bind === 'function') || ++tries > 20) clearInterval(t)
    }, 500)
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true })
  } else {
    schedule()
  }

  // The storefront SDK can re-mount slot wrappers after load (conflict
  // resolution / vis flips) — a replaced wrapper holds a fresh unpainted
  // element, so rebind on DOM changes, debounced.
  const __saiRelevant = (records) => {
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n.nodeType !== 1) continue
        if ((n.matches && n.matches(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"], [data-spectrum-lq-snippet]`)) ||
            (n.querySelector && n.querySelector(`${TAG}, [data-spectrum-snippet-id="${SNIPPET_ID}"]`))) return true
      }
    }
    return false
  }
  const __saiMo = new MutationObserver((records) => {
    if (__saiMo._t || !__saiRelevant(records)) return
    __saiMo._t = setTimeout(() => { __saiMo._t = null; bindAll() }, 150)
  })
  __saiMo.observe(document.documentElement, { childList: true, subtree: true })
  setTimeout(function () { try { __saiMo.disconnect() } catch (e) {} }, 30000)
})()
